import { useEffect, useState } from 'preact/hooks';

/**
 * A ticking clock aligned to real boundaries.
 *
 * Two details that matter on a phone: ticks are aligned to the next whole
 * second/minute so the display never drifts a half-beat behind the system
 * clock, and the timer is torn down while the tab is hidden so a HUD left open
 * on a home screen doesn't burn battery.
 */
export function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const scheduleNext = () => {
      const current = Date.now();
      const delay = intervalMs - (current % intervalMs);
      timeout = setTimeout(() => {
        if (cancelled) return;
        setNow(new Date());
        scheduleNext();
      }, delay);
    };

    const start = () => {
      setNow(new Date());
      scheduleNext();
    };

    const stop = () => {
      if (timeout !== undefined) clearTimeout(timeout);
      timeout = undefined;
    };

    const onVisibility = () => {
      stop();
      if (document.visibilityState === 'visible') start();
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs]);

  return now;
}
